import React, { useEffect, useState } from 'react';
import { Col, message, Row, Typography, Divider, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import ENDPOINTS from '../../constants/endpoints';
import api from '../../services/api';
import useDefaultRequest from '../../hooks/useDefaultRequest';
import Form from '../../components/data_entry/form/Form';
import PdfLink from '../../components/PdfLink';
import InputFormField from '../../components/data_entry/form/InputFormField';
import TextAreaFormField from '../../components/data_entry/form/TextAreaFormField';
import Button from '../../components/data_entry/Button';
import Loading from '../../components/admin/Loading';
import { BuildFieldErrorsDict } from '../../utils/utils';

const FORM_NAMES = {
    YOUTUBE_URL: 'youtube_url',
    WHATSAPP: 'whatsapp',
    INSTAGRAM_URL: 'instagram_url',
    TWITTER_URL: 'twitter_url',
    PHONE: 'phone',
    EMAIL: 'email',
    ADDRESS: 'address',
    WORKING_HOURS: 'working_hours',
    MAP_URL: 'map_url',
    JUDICIAL_TERM_PDF: 'judicial_term_pdf',
    CONSENT_TERM_PDF: 'consent_term_pdf',
};

export default function AdminSiteSettings() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const { data, isLoading: fetching } = useDefaultRequest({
        url: ENDPOINTS.SITE_SETTINGS,
    });

    useEffect(() => {
        if (data) {
            const { judicial_term_pdf, consent_term_pdf, ...rest } = data;
            form.setFieldsValue(rest);
        }
    }, [data, form]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formData = new FormData();
            Object.entries(values).forEach(([key, value]) => {
                if (key === FORM_NAMES.JUDICIAL_TERM_PDF || key === FORM_NAMES.CONSENT_TERM_PDF) {
                    if (value && value.length > 0) {
                        const file = value[0].originFileObj || value[0];
                        formData.append(key, file);
                    }
                } else if (value != null) {
                    formData.append(key, value);
                }
            });
            await api.patch(ENDPOINTS.SITE_SETTINGS, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            message.success('Configurações salvas com sucesso');
        } catch (error) {
            BuildFieldErrorsDict(error, form.setFields);
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        beforeUpload: () => false,
        maxCount: 1,
        accept: '.pdf',
    };

    const normFile = (e) => {
        if (Array.isArray(e)) return e;
        return e?.fileList;
    };

    if (fetching) {
        return <Loading />;
    }

    return (
        <Row>
            <Col xs={24}>
                <Typography.Title style={{ marginTop: 0 }} level={2}>
                    Configurações do Site
                </Typography.Title>
                <Typography.Text type="secondary">
                    Gerencie as informações de contato e redes sociais exibidas no site.
                </Typography.Text>
            </Col>

            <Col xs={24} style={{ marginTop: 24 }}>
                <Form form={form} onFinish={onFinish}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24}>
                            <Divider orientation="left">Informações de Contato</Divider>
                        </Col>

                        <Col xs={24} md={12}>
                            <InputFormField
                                placeholder="(11) 3333-4444"
                                form={{
                                    label: 'Telefone',
                                    name: FORM_NAMES.PHONE,
                                }}
                            />
                        </Col>

                        <Col xs={24} md={12}>
                            <InputFormField
                                placeholder="99999-9999"
                                form={{
                                    label: 'WhatsApp',
                                    name: FORM_NAMES.WHATSAPP,
                                }}
                            />
                        </Col>

                        <Col xs={24} md={12}>
                            <InputFormField
                                placeholder="contato@cebrapcam.org.br"
                                form={{
                                    label: 'E-mail',
                                    name: FORM_NAMES.EMAIL,
                                }}
                            />
                        </Col>

                        <Col xs={24} md={12}>
                            <InputFormField
                                placeholder="Seg - Sex: 8h as 18h"
                                form={{
                                    label: 'Horario de Funcionamento',
                                    name: FORM_NAMES.WORKING_HOURS,
                                }}
                            />
                        </Col>

                        <Col xs={24}>
                            <TextAreaFormField
                                rows={2}
                                placeholder="Rua Exemplo, 123 - Centro, Sao Paulo - SP"
                                form={{
                                    label: 'Endereco',
                                    name: FORM_NAMES.ADDRESS,
                                }}
                            />
                        </Col>

                        <Col xs={24}>
                            <Divider orientation="left">Redes Sociais</Divider>
                        </Col>

                        <Col xs={24} md={12}>
                            <InputFormField
                                placeholder="https://www.instagram.com/cebrapcam"
                                form={{
                                    label: 'URL do Instagram',
                                    name: FORM_NAMES.INSTAGRAM_URL,
                                }}
                            />
                        </Col>

                        <Col xs={24} md={12}>
                            <InputFormField
                                placeholder="https://www.youtube.com/@cebrapcam"
                                form={{
                                    label: 'URL do YouTube',
                                    name: FORM_NAMES.YOUTUBE_URL,
                                }}
                            />
                        </Col>

                        <Col xs={24} md={12}>
                            <InputFormField
                                placeholder="https://twitter.com/cebrapcam"
                                form={{
                                    label: 'URL do Twitter',
                                    name: FORM_NAMES.TWITTER_URL,
                                }}
                            />
                        </Col>

                        <Col xs={24}>
                            <Divider orientation="left">Mapa</Divider>
                        </Col>

                        <Col xs={24}>
                            <TextAreaFormField
                                rows={2}
                                placeholder="https://www.google.com/maps/embed?pb=..."
                                form={{
                                    label: 'URL do Mapa (Google Maps Embed)',
                                    name: FORM_NAMES.MAP_URL,
                                }}
                            />
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Cole a URL de incorporacao do Google Maps. Se vazio, a secao do mapa sera ocultada na home.
                            </Typography.Text>
                        </Col>

                        <Form.Item noStyle shouldUpdate={(prev, curr) => prev[FORM_NAMES.MAP_URL] !== curr[FORM_NAMES.MAP_URL]}>
                            {({ getFieldValue }) => {
                                const mapUrl = getFieldValue(FORM_NAMES.MAP_URL);
                                return mapUrl ? (
                                    <Col xs={24} style={{ marginTop: 16 }}>
                                        <Typography.Text strong>Preview do Mapa:</Typography.Text>
                                        <div style={{ marginTop: 8, border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
                                            <iframe
                                                src={mapUrl}
                                                width="100%"
                                                height="300"
                                                style={{ border: 0 }}
                                                allowFullScreen=""
                                                loading="lazy"
                                                referrerPolicy="no-referrer-when-downgrade"
                                                title="Preview do Mapa"
                                            />
                                        </div>
                                    </Col>
                                ) : null;
                            }}
                        </Form.Item>

                        <Col xs={24}>
                            <Divider orientation="left">Termos</Divider>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Termo Judicial (PDF)"
                                name={FORM_NAMES.JUDICIAL_TERM_PDF}
                                valuePropName="fileList"
                                getValueFromEvent={normFile}
                                rules={[{ required: !data?.judicial_term_pdf, message: 'Campo obrigatório' }]}
                            >
                                <Upload {...uploadProps}>
                                    <Button icon={<UploadOutlined />}>Selecionar arquivo</Button>
                                </Upload>
                            </Form.Item>
                            {data?.judicial_term_pdf && (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    Arquivo atual: <PdfLink url={data.judicial_term_pdf}/>
                                </Typography.Text>
                            )}
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Termo de Consentimento (PDF)"
                                name={FORM_NAMES.CONSENT_TERM_PDF}
                                valuePropName="fileList"
                                getValueFromEvent={normFile}
                                rules={[{ required: !data?.consent_term_pdf, message: 'Campo obrigatório' }]}
                            >
                                <Upload {...uploadProps}>
                                    <Button icon={<UploadOutlined />}>Selecionar arquivo</Button>
                                </Upload>
                            </Form.Item>
                            {data?.consent_term_pdf && (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    Arquivo atual: <PdfLink url={data.consent_term_pdf}/>
                                </Typography.Text>
                            )}
                        </Col>

                        <Col xs={24} style={{ textAlign: 'right', marginTop: 24 }}>
                            <Button onClick={form.submit} loading={loading}>
                                Salvar Configurações
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Col>
        </Row>
    );
}